// Drawing a laid-out map as SVG. The SAME markup draws the live canvas and the
// saved file, so what you see is exactly what Obsidian, Notion and a download
// show. Everything is plain SVG (no foreignObject, no web fonts), so it renders
// inside an <img> anywhere.
import { Link, StickyNote } from 'lucide';
import { FONT } from './theme.js';
import { badgePos, PLACEHOLDER } from './layout.js';

export const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const r1 = (v) => Math.round(v * 10) / 10;

function iconMarkup(node, x, y, size, color) {
  const inner = node.map(([tag, attrs]) => `<${tag} ${Object.entries(attrs).map(([k, v]) => `${k}="${esc(v)}"`).join(' ')}/>`).join('');
  return `<g transform="translate(${r1(x)} ${r1(y)}) scale(${r1(size / 24 * 100) / 100})" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${inner}</g>`;
}

// ---- edges ------------------------------------------------------------------------------

/** The branch line from parent box p to child box c, in the map's line style. */
export function edgePath(p, c, style) {
  const pl = p.st.mode === 'line'; const cl = c.st.mode === 'line';
  const dir = c.dir;
  let sx; let sy; let ex; let ey;
  if (dir === 'bubble') return circleLink(p, c, style === 'curved' ? 0.12 : 0);
  if (dir === 'down' || dir === 'up') {
    sx = p.x + p.w / 2; ex = c.x + c.w / 2;
    if (dir === 'down') { sy = p.y + p.h; ey = c.y; } else { sy = p.y; ey = c.y + c.h; }
    return vertical(sx, sy, ex, ey, style);
  }
  if (dir === 'tree') {
    sx = p.x + Math.min(16, p.w / 2); sy = p.y + p.h; ex = c.x; ey = cl ? c.y + c.h : c.y + c.h / 2;
    const r = Math.min(8, Math.abs(ey - sy) / 2, Math.abs(ex - sx));
    if (style === 'straight') return `M${r1(sx)} ${r1(sy)}L${r1(ex)} ${r1(ey)}`;
    if (style === 'elbow') return `M${r1(sx)} ${r1(sy)}V${r1(ey)}H${r1(ex)}`;
    return `M${r1(sx)} ${r1(sy)}V${r1(ey - r)}Q${r1(sx)} ${r1(ey)} ${r1(sx + r)} ${r1(ey)}H${r1(ex)}`;
  }
  if (dir === 'left') { sx = p.x; ex = c.x + c.w; } else { sx = p.x + p.w; ex = c.x; }
  sy = pl ? p.y + p.h : p.y + p.h / 2;
  ey = cl ? c.y + c.h : c.y + c.h / 2;
  // underlined topics: the line runs on along the child's underline, so start at its near corner
  return horizontal(sx, sy, ex, ey, style);
}
function horizontal(sx, sy, ex, ey, style) {
  const mx = sx + (ex - sx) / 2;
  if (style === 'straight') return `M${r1(sx)} ${r1(sy)}L${r1(ex)} ${r1(ey)}`;
  if (style === 'elbow') return `M${r1(sx)} ${r1(sy)}H${r1(mx)}V${r1(ey)}H${r1(ex)}`;
  if (style === 'rounded') {
    const dy = ey - sy; const dx = ex - sx;
    if (Math.abs(dy) < 1) return `M${r1(sx)} ${r1(sy)}H${r1(ex)}`;
    const r = Math.min(10, Math.abs(dy) / 2, Math.abs(dx) / 2);
    const gx = Math.sign(dx); const gy = Math.sign(dy);
    return `M${r1(sx)} ${r1(sy)}H${r1(mx - gx * r)}Q${r1(mx)} ${r1(sy)} ${r1(mx)} ${r1(sy + gy * r)}V${r1(ey - gy * r)}Q${r1(mx)} ${r1(ey)} ${r1(mx + gx * r)} ${r1(ey)}H${r1(ex)}`;
  }
  return `M${r1(sx)} ${r1(sy)}C${r1(mx)} ${r1(sy)} ${r1(mx)} ${r1(ey)} ${r1(ex)} ${r1(ey)}`;
}
function vertical(sx, sy, ex, ey, style) {
  const my = sy + (ey - sy) / 2;
  if (style === 'straight') return `M${r1(sx)} ${r1(sy)}L${r1(ex)} ${r1(ey)}`;
  if (style === 'elbow') return `M${r1(sx)} ${r1(sy)}V${r1(my)}H${r1(ex)}V${r1(ey)}`;
  if (style === 'rounded') {
    const dx = ex - sx; const dy = ey - sy;
    if (Math.abs(dx) < 1) return `M${r1(sx)} ${r1(sy)}V${r1(ey)}`;
    const r = Math.min(10, Math.abs(dx) / 2, Math.abs(dy) / 2);
    const gx = Math.sign(dx); const gy = Math.sign(dy);
    return `M${r1(sx)} ${r1(sy)}V${r1(my - gy * r)}Q${r1(sx)} ${r1(my)} ${r1(sx + gx * r)} ${r1(my)}H${r1(ex - gx * r)}Q${r1(ex)} ${r1(my)} ${r1(ex)} ${r1(my + gy * r)}V${r1(ey)}`;
  }
  return `M${r1(sx)} ${r1(sy)}C${r1(sx)} ${r1(my)} ${r1(ex)} ${r1(my)} ${r1(ex)} ${r1(ey)}`;
}
const edgeWidth = (p) => (p.dot ? (p.depth === 0 ? 1.8 : 1.3) : p.depth === 0 ? 2.75 : p.depth === 1 ? 2 : 1.5);

/** A line between two circles, from rim to rim (bent a little when `bend`). */
function circleLink(a, b, bend = 0, gapA = 0, gapB = 0) {
  const ax = a.x + a.w / 2; const ay = a.y + a.h / 2; const bx = b.x + b.w / 2; const by = b.y + b.h / 2;
  const dx = bx - ax; const dy = by - ay; const d = Math.hypot(dx, dy) || 1;
  const ux = dx / d; const uy = dy / d;
  const ra = a.w / 2 + gapA; const rb = b.w / 2 + gapB;
  if (d <= ra + rb) return `M${r1(ax)} ${r1(ay)}`;
  const sx = ax + ux * ra; const sy = ay + uy * ra; const ex = bx - ux * rb; const ey = by - uy * rb;
  if (!bend) return `M${r1(sx)} ${r1(sy)}L${r1(ex)} ${r1(ey)}`;
  const mx = (sx + ex) / 2 - uy * d * bend; const my = (sy + ey) / 2 + ux * d * bend;
  return `M${r1(sx)} ${r1(sy)}Q${r1(mx)} ${r1(my)} ${r1(ex)} ${r1(ey)}`;
}

// ---- relationships ---------------------------------------------------------------------

function edgePoint(b, tx, ty, pad = 5) {
  const cx = b.x + b.w / 2; const cy = b.y + b.h / 2;
  const dx = tx - cx; const dy = ty - cy;
  if (!dx && !dy) return { x: cx, y: cy };
  if (b.circle) { const d = Math.hypot(dx, dy); return { x: cx + (dx / d) * (b.w / 2 + pad), y: cy + (dy / d) * (b.w / 2 + pad) }; }
  const t = Math.min((b.w / 2 + pad) / Math.abs(dx || 1e-9), (b.h / 2 + pad) / Math.abs(dy || 1e-9));
  return { x: cx + dx * t, y: cy + dy * t };
}
/** A relationship's curve between two boxes: { d, lx, ly } (label position at the middle). */
export function relGeom(a, b, rel) {
  const ca = { x: a.x + a.w / 2, y: a.y + a.h / 2 }; const cb = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
  const dx = cb.x - ca.x; const dy = cb.y - ca.y;
  const dist = Math.hypot(dx, dy) || 1;
  const bend = rel.bend == null ? (a.circle ? 0.15 : 0.25) : Number(rel.bend) || 0;
  const mx = (ca.x + cb.x) / 2 - (dy / dist) * dist * bend; const my = (ca.y + cb.y) / 2 + (dx / dist) * dist * bend;
  const s = edgePoint(a, mx, my); const e = edgePoint(b, mx, my, 9);
  return { d: `M${r1(s.x)} ${r1(s.y)}Q${r1(mx)} ${r1(my)} ${r1(e.x)} ${r1(e.y)}`, lx: (s.x + 2 * mx + e.x) / 4, ly: (s.y + 2 * my + e.y) / 4 };
}

// ---- topics ------------------------------------------------------------------------------

function shapeMarkup(b, extraCls = '') {
  const { st, w, h } = b;
  const common = `fill="${st.fill}"${st.strokeW ? ` stroke="${st.stroke}" stroke-width="${st.strokeW}"` : ''}`;
  if (st.mode === 'line') {
    if (st.shape === 'none') return `<rect class="mm-hit${extraCls}" width="${w}" height="${h}" fill="transparent"/>`;
    return `<rect class="mm-hit${extraCls}" width="${w}" height="${h}" fill="transparent"/><path d="M0 ${h}H${w}" stroke="${st.line}" stroke-width="2" stroke-linecap="round"/>`;
  }
  if (st.shape === 'ellipse') return `<ellipse class="mm-shape${extraCls}" cx="${w / 2}" cy="${h / 2}" rx="${w / 2}" ry="${h / 2}" ${common}/>`;
  if (st.shape === 'circle') return `<circle class="mm-shape${extraCls}" cx="${w / 2}" cy="${h / 2}" r="${w / 2}" ${common}/>`;
  const rx = st.shape === 'pill' ? h / 2 : st.radius;
  return `<rect class="mm-shape${extraCls}" width="${w}" height="${h}" rx="${r1(rx)}" ${common}/>`;
}

/** A dot (bubble map, dots style): the circle, and its label under it on a halo. */
function dotMarkup(b, T, cls, interactive, editing) {
  const { nd, st, w, h, lines } = b;
  const done = nd.task === 'done';
  let s = `<g class="${cls} is-dot" ${interactive ? `data-id="${esc(b.id)}" ` : ''}transform="translate(${r1(b.x)} ${r1(b.y)})">`;
  s += `<circle class="mm-shape" cx="${w / 2}" cy="${h / 2}" r="${w / 2}" fill="${st.fill}"/>`;
  if (interactive) s += `<rect class="mm-hit" x="${r1(w / 2 - b.lab.w / 2 - 4)}" y="${h}" width="${r1(b.lab.w + 8)}" height="${b.lab.h + 4}" fill="transparent"/>`;
  if (editing === b.id) return `${s}</g>`;
  const empty = !nd.text;
  if (empty && !interactive) return `${s}</g>`;
  let tx = w / 2;
  if (nd.task) {
    const sz = Math.round(st.fontSize * 0.85); const bx = w / 2 - b.lab.w / 2; const by = h + 4 + st.lineH / 2 - sz / 2;
    s += `<g ${interactive ? `data-task="${esc(b.id)}" ` : ''}class="mm-task"><rect x="${r1(bx)}" y="${r1(by)}" width="${sz}" height="${sz}" rx="3" fill="${done ? st.fill : T.bg}" stroke="${st.fill}" stroke-width="1.5"/></g>`;
    tx += 9;
  }
  s += `<text class="mm-glabel" x="${r1(tx)}" text-anchor="middle" font-family="${esc(FONT)}" font-size="${st.fontSize}" font-weight="${st.weight}"${st.italic ? ' font-style="italic"' : ''} fill="${done ? T.muted : T.ink}" stroke="${T.bg}" stroke-width="3" stroke-linejoin="round" paint-order="stroke"${empty ? ' opacity="0.4"' : ''}${done ? ' text-decoration="line-through"' : ''}>`;
  (empty ? [PLACEHOLDER] : lines).forEach((line, i) => { s += `<tspan x="${r1(tx)}" y="${r1(h + 4 + i * st.lineH + st.lineH / 2)}" dominant-baseline="central">${esc(line)}</tspan>`; });
  return `${s}</text></g>`;
}

function topicMarkup(b, T, { interactive, dim, hit, current, editing, fade }) {
  const { nd, st, w, h, lines } = b;
  const done = nd.task === 'done';
  const cls = `mm-node${dim?.has(b.id) ? ' is-dim' : ''}${hit?.has(b.id) ? ' is-hit' : ''}${current === b.id ? ' is-current' : ''}${fade && !fade.keep.has(b.id) ? ' is-fade' : ''}${fade && fade.id === b.id ? ' is-hl' : ''}`;
  if (b.dot) return dotMarkup(b, T, cls, interactive, editing);
  let s = `<g class="${cls}" ${interactive ? `data-id="${esc(b.id)}" ` : ''}transform="translate(${r1(b.x)} ${r1(b.y)})">`;
  s += shapeMarkup(b);
  const contentW = b.left + b.textW + b.right;
  const x0 = (w - contentW) / 2;
  const textTop = (h - lines.length * st.lineH) / 2;
  const ink = done ? T.muted : st.ink;
  if (nd.task) {
    const bx = x0; const by = textTop + st.lineH / 2 - b.icon / 2; const sz = b.icon;
    const boxInk = st.mode === 'solid' || st.mode === 'root' ? st.ink : (b.color || T.ink);
    s += `<g ${interactive ? `data-task="${esc(b.id)}" ` : ''}class="mm-task"><rect x="${r1(bx)}" y="${r1(by)}" width="${sz}" height="${sz}" rx="${r1(sz * 0.22)}" fill="${done ? boxInk : 'none'}" stroke="${boxInk}" stroke-width="1.6"/>`;
    if (done) s += `<path d="M${r1(bx + sz * 0.24)} ${r1(by + sz * 0.52)}l${r1(sz * 0.18)} ${r1(sz * 0.18)}l${r1(sz * 0.34)} ${r1(-sz * 0.38)}" fill="none" stroke="${st.mode === 'solid' || st.mode === 'root' ? st.fill : T.bg}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`;
    s += '</g>';
  }
  const tx = x0 + b.left + b.textW / 2;
  const empty = !nd.text;
  if ((!empty || interactive) && editing !== b.id) {
    s += `<text x="${r1(tx)}" text-anchor="middle" font-family="${esc(FONT)}" font-size="${st.fontSize}" font-weight="${st.weight}"${st.italic ? ' font-style="italic"' : ''} fill="${ink}"${empty ? ' opacity="0.4"' : ''}${done ? ' text-decoration="line-through"' : ''}>`;
    (empty ? [PLACEHOLDER] : lines).forEach((line, i) => { s += `<tspan x="${r1(tx)}" y="${r1(textTop + i * st.lineH + st.lineH / 2)}" dominant-baseline="central">${esc(line)}</tspan>`; });
    s += '</text>';
  }
  let ix = x0 + b.left + b.textW + 6;
  const iy = textTop + st.lineH / 2 - b.icon / 2;
  const iconInk = st.mode === 'solid' || st.mode === 'root' ? st.ink : T.muted;
  if (nd.link) { s += `<g class="mm-ic" ${interactive ? `data-link="${esc(b.id)}"` : ''}><rect x="${r1(ix - 2)}" y="${r1(iy - 2)}" width="${b.icon + 4}" height="${b.icon + 4}" fill="transparent"/>${iconMarkup(Link, ix, iy, b.icon, iconInk)}</g>`; ix += b.icon + 6; }
  if (nd.note) { s += `<g class="mm-ic" ${interactive ? `data-note="${esc(b.id)}"` : ''}><rect x="${r1(ix - 2)}" y="${r1(iy - 2)}" width="${b.icon + 4}" height="${b.icon + 4}" fill="transparent"/>${iconMarkup(StickyNote, ix, iy, b.icon, iconInk)}</g>`; ix += b.icon + 6; }
  if (b.progText) s += `<text x="${r1(ix + 4)}" y="${r1(textTop + st.lineH / 2)}" dominant-baseline="central" font-family="${esc(FONT)}" font-size="${Math.round(st.fontSize * 0.78)}" font-weight="600" fill="${iconInk}">${esc(b.progText)}</text>`;
  s += '</g>';
  return s;
}

function badgeMarkup(b, T, interactive) {
  const p = badgePos(b);
  const label = b.hidden > 99 ? '99+' : String(b.hidden);
  const rw = Math.max(20, 8 + label.length * 7);
  const col = b.color || T.ink;
  return `<g class="mm-badge" ${interactive ? `data-toggle="${esc(b.id)}" ` : ''}transform="translate(${r1(p.x)} ${r1(p.y)})"><rect x="${-rw / 2}" y="-10" width="${rw}" height="20" rx="10" fill="${T.bg}" stroke="${col}" stroke-width="1.5"/><text y="0.5" text-anchor="middle" dominant-baseline="central" font-family="${esc(FONT)}" font-size="11" font-weight="600" fill="${col}">${label}</text></g>`;
}

/**
 * The map's drawing (edges, relationships, topics, badges), in map units.
 * interactive: data-* hooks for the editor and placeholders on empty topics.
 */
export function contentMarkup(map, L, opts = {}) {
  const T = L.theme;
  const { interactive = false, dim = null, hit = null, current = null, selRel = null, editing = null, fade = null } = opts;
  let s = '<g class="mm-edges" fill="none" stroke-linecap="round">';
  for (const e of L.edges) {
    const p = L.boxes.get(e.from); const c = L.boxes.get(e.to);
    if (!p || !c) continue;
    const lit = fade && (e.from === fade.id || e.to === fade.id);
    const col = c.dot ? c.st.fill : c.st.line;
    s += `<path d="${edgePath(p, c, map.line)}" stroke="${col}" stroke-width="${edgeWidth(p) * (lit ? 1.6 : 1)}"${dim?.has(c.id) ? ' opacity="0.3"' : c.dot && !lit ? ' opacity="0.55"' : ''}${fade && !lit ? ' class="is-fade"' : ''}/>`;
  }
  s += '</g><g class="mm-topics">';
  for (const b of L.boxes.values()) s += topicMarkup(b, T, { interactive, dim, hit, current, editing, fade });
  for (const b of L.boxes.values()) if (b.hidden) s += badgeMarkup(b, T, interactive);
  // relationships go over the topics (as in XMind), so their lines and labels always show
  s += '</g><g class="mm-rels">';
  for (const r of L.rels) {
    const a = L.boxes.get(r.from); const b = L.boxes.get(r.to);
    const g = relGeom(a, b, r);
    const col = T.rel;
    const relLit = !fade || r.from === fade.id || r.to === fade.id;
    s += `<g class="mm-rel${selRel === r.id ? ' is-sel' : ''}${relLit ? '' : ' is-fade'}"${interactive ? ` data-rel="${esc(r.id)}"` : ''}>`;
    if (interactive) s += `<path d="${g.d}" fill="none" stroke="transparent" stroke-width="14"/>`;
    s += `<path class="mm-rel-line" d="${g.d}" fill="none" stroke="${col}" stroke-width="1.6" stroke-dasharray="6 5" marker-end="url(#mm-arrow)"/>`;
    if (r.label) {
      const lw = Math.min(220, r.label.length * 7 + 16);
      s += `<rect x="${r1(g.lx - lw / 2)}" y="${r1(g.ly - 11)}" width="${r1(lw)}" height="22" rx="6" fill="${T.bg}"/><text x="${r1(g.lx)}" y="${r1(g.ly)}" text-anchor="middle" dominant-baseline="central" font-family="${esc(FONT)}" font-size="12" font-weight="500" fill="${col}">${esc(r.label.length > 32 ? `${r.label.slice(0, 31)}...` : r.label)}</text>`;
    }
    s += '</g>';
  }
  s += '</g>';
  return s;
}

export const arrowDefs = (T) => `<defs><marker id="mm-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M1 1L9 5L1 9" fill="none" stroke="${T.rel}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>`;

/**
 * The whole picture as an SVG document string: the map's drawing on its theme
 * background (or transparent), `pad` around it, and `metadata` (the file's
 * saved map, see engine/io.js) inside.
 */
export function svgDocument(map, L, { pad = 48, transparent = false, metadata = '', title = '' } = {}) {
  const T = L.theme;
  const W = Math.ceil(L.bounds.w + pad * 2); const H = Math.ceil(L.bounds.h + pad * 2);
  const ox = r1(pad - L.bounds.x); const oy = r1(pad - L.bounds.y);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`
    + `${title ? `<title>${esc(title)}</title>` : ''}${metadata}${arrowDefs(T)}`
    + `${transparent ? '' : `<rect width="${W}" height="${H}" fill="${T.bg}"/>`}`
    + `<g transform="translate(${ox} ${oy})">${contentMarkup(map, L)}</g></svg>`;
}
